/**
 * "Play this on the TV", received.
 *
 * Lives above the routes rather than on the player, because the point of the feature is
 * that the television is *not* on the right screen — it is on the home page, or on a
 * different film, or showing a menu somebody left open. A listener attached to the player
 * would only work once the viewer had already navigated to a player, which is the case
 * that needed no help.
 *
 * ## The position is a hint, not the truth
 *
 * The message may carry `positionSec` from the phone, and it is deliberately ignored here:
 * the player resumes from `WatchProgress`, which is the household's cross-device record
 * and already governs exactly this case. Passing the phone's number through would let a
 * stale reading from a screen that has been in a pocket overwrite a position the server
 * knows better.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeChannel } from './RealtimeContext';

export function useTvHandoff(enabled: boolean): void {
  const navigate = useNavigate();
  const { subscribe } = useRealtimeChannel();

  useEffect(() => {
    if (!enabled) return;

    return subscribe((message) => {
      if (message.type !== 'playOnTv') return;

      /*
       * `replace: false`, so Back returns to whatever was on screen.
       *
       * The alternative — replacing the entry — would leave the television with nowhere to
       * go back to after a film somebody else started from the kitchen, and Back on a TV
       * that has nothing to go back to is a dead button.
       */
      void navigate(`/watch/${message.itemId}`);
    });
  }, [enabled, subscribe, navigate]);
}
